import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UserIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { clientService } from '../../services/clientService';
import { cn } from '../../utils/cn';

const ClientSearchSelect = ({
  value,
  onChange,
  placeholder = 'Buscar cliente...',
  error = null,
  required = false,
  className = '',
}) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const hasValue = value !== '' && value != null && String(value).trim() !== '';

  const { data: clientById, isFetching: fetchingClientById } = useQuery({
    queryKey: ['client-detail', value],
    queryFn: () => clientService.getClient(value),
    enabled: hasValue,
  });

  // Carga al montar (no solo al abrir): evita spinner colgado si el foco/backdrop corta el flujo
  const {
    data: clientsRaw,
    isFetching: fetchingList,
    isError: listError,
    error: listErr,
  } = useQuery({
    queryKey: ['clients-search', searchTerm],
    queryFn: () => clientService.getClients({ search: searchTerm }),
    enabled: true,
    staleTime: 30_000,
  });

  const clients = useMemo(
    () => (Array.isArray(clientsRaw) ? clientsRaw : []),
    [clientsRaw]
  );

  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients;
    const q = searchTerm.toLowerCase();
    return clients.filter(
      (client) =>
        client.name?.toLowerCase().includes(q) ||
        client.email?.toLowerCase().includes(q) ||
        client.company?.toLowerCase().includes(q)
    );
  }, [clients, searchTerm]);

  const selectedClient = useMemo(() => {
    if (clientById && hasValue) return clientById;
    return clients.find((client) => String(client.id) === String(value));
  }, [clientById, clients, value, hasValue]);

  useEffect(() => {
    if (!isOpen) return;
    const close = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [isOpen]);

  const listLoading = fetchingList && clientsRaw === undefined;
  const showListSpinner = isOpen && listLoading;

  const handleSelect = (client) => {
    onChange(client.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onChange('');
    setSearchTerm('');
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative z-10">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <UserIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          autoComplete="off"
          value={
            selectedClient && hasValue
              ? `${selectedClient.name} - ${selectedClient.email}`
              : searchTerm
          }
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className={cn(
            'w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            error ? 'border-red-300' : 'border-gray-300'
          )}
          required={required}
        />

        {hasValue && selectedClient && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 z-20"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {hasValue && fetchingClientById && !selectedClient && (
        <p className="text-xs text-gray-500 mt-1">Cargando cliente…</p>
      )}

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}

      {isOpen && (
        <div
          className="absolute left-0 right-0 z-[9999] mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
          role="listbox"
        >
          {showListSpinner ? (
            <div className="p-3 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
              <p className="mt-2 text-sm">Buscando clientes...</p>
            </div>
          ) : listError ? (
            <div className="p-3 text-center text-sm text-red-600">
              No se pudieron cargar los clientes
              {listErr?.message ? `: ${listErr.message}` : ''}
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="p-3 text-center text-gray-500">
              <p className="text-sm">No se encontraron clientes</p>
              {searchTerm ? (
                <p className="text-xs mt-1">Intenta con otro término de búsqueda</p>
              ) : null}
            </div>
          ) : (
            <div className="py-1">
              {filteredClients.map((client) => (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => handleSelect(client)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <UserIcon className="h-4 w-4 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{client.name}</p>
                      <div className="flex items-center text-xs text-gray-500">
                        <BuildingOfficeIcon className="h-3 w-3 mr-1" />
                        <span className="truncate">{client.company}</span>
                      </div>
                      <p className="text-xs text-gray-500 truncate">{client.email}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                          client.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        )}
                      >
                        {client.status === 'active' ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { ClientSearchSelect };
